import types from "./types";
import { updateObject } from "utils";
import { upgradePackageSort } from "./selectors";
import { cloneDeep } from "lodash";
import { getSelectedComponents } from "utils/communities";
import { getCompAreaForCostCalc } from "utils/enclosure/components";
import { overallCostTypes } from "utils/costTypeIndex";
import { calcUpgradeDetailedCost } from "utils/upgrades";

export const initialState = {
    baseModelId: null,
    capitalCosts: {},
    packages: {},
    savedUpgradePackages: {},
    initPackages: {},
    initPackagesSnapshot: {},
    loading: false,
    error: false,
    isSavingUpgrades: false,
};

export default (state = initialState, action) => {
    switch (action.type) {
        case types.CLEAR_COMM_UPGRADE_DATA:
            return clearUpgradeData(state, action);
        case types.TOGGLE_COMM_UPGRADES_LOADING:
            return toggleUpgradesLoading(state, action);
        case types.SET_COMM_UPGRADES_ERROR:
            return setUpgradesError(state, action);
        case types.SET_COMM_BASE_MODEL_ID:
            return setBaseModelId(state, action);
        case types.ADD_COMM_UPGRADE_PACKAGE:
        case types.UPDATE_COMM_UPGRADE_PACKAGE:
            return updateUpgradePackage(state, action);
        case types.DELETE_COMM_UPGRADE_PACKAGE:
            return deleteUpgradePackage(state, action);
        case types.SET_COMM_UPGRADE_PACKAGES:
            return setUpgradePackages(state, action);
        case types.UPDATE_COMM_PACKAGE_UPGRADES:
            return updatePackageUpgrades(state, action);
        case types.SET_INIT_COMM_UPGRADE_PACKAGES:
            return setInitUpgradePackages(state, action);
        case types.DELETE_COMM_PACKAGE_UPGRADE:
            return deletePackageUpgrade(state, action);
        case types.UPDATE_COMM_UPGRADE_PACKAGE_ORDER:
            return reorderUpgradePackages(state, action);
        case types.UPDATE_COMM_UPGRADE_COST:
            return updateUpgradeCost(state, action);
        case types.UPDATE_COMM_PACKAGE_OPERATING_CONDITIONS:
            return updatePackageOperatingConditions(state, action);
        case types.UPDATE_COMM_PACKAGE_BASE_CASE:
            return updatePackageBaseCase(state, action);
        case types.UPDATE_COMM_UPGRADE_SNAPSHOT_SUCCESS:
            return updateUpgradeSnapshot(state, action);
        case types.ADJUST_COMM_PACKAGE_COST:
            return updatePackageCost(state, action);
        case types.INIT_ALL_CAPITAL_COSTS:
            return initCapitalCosts(state, action);
        case types.REMOVE_COMM_MODEL_PACKAGE_COSTS:
            return removeCommModelPackageCosts(state, action);
        case types.CALC_COMM_MODEL_PACKAGE_COST:
            return addCommModelPackageCost(state, action);
        case types.SAVE_COMM_UPGRADES_START:
            return updateObject(state, {
                isSavingUpgrades: true,
                error: null,
            });
        case types.SAVE_COMM_UPGRADES_SUCCESS:
            return updateObject(state, {
                isSavingUpgrades: false,
            });
        default:
            return state;
    }
};

const clearUpgradeData = (state, action) => initialState;

const toggleUpgradesLoading = (state, action) =>
    updateObject(state, {
        loading: action.isLoading,
    });

const setUpgradesError = (state, action) =>
    updateObject(state, {
        error: action.error,
    });

const setBaseModelId = (state, action) =>
    updateObject(state, {
        baseModelId: action.payload.baseModelId,
    });

const updateUpgradePackage = (state, action) =>
    updateObject(state, {
        packages: updateObject(state.packages, {
            [action.packageId]: {
                ...(state.packages[action.packageId] ? state.packages[action.packageId] : {}),
                ...action.packageUpdates,
            },
        }),
    });

const updatePackageUpgrades = (state, action) => {
    return updateObject(state, {
        packages: updateObject(state.packages, {
            [action.packageId]: {
                ...state.packages[action.packageId],
                lastEdited: action?.dateTime,
                upgrades: {
                    ...(state.packages[action.packageId]?.upgrades ? state.packages[action.packageId].upgrades : {}),
                    ...action.upgrades,
                },
            },
        }),
    });
};

const deleteUpgradePackage = (state, action) => {
    const removedPackageArray = Object.keys(state.packages)
        .filter((id) => id !== action.packageId)
        .map((packageId) => ({
            id: packageId,
            ...state.packages[packageId],
        }))
        .sort(upgradePackageSort);

    const packagesObj = removedPackageArray.reduce((obj, { id, ui = {}, ...rest }, index) => {
        return {
            ...obj,
            [id]: {
                ui: {
                    displayOrder: Math.abs(index - (removedPackageArray.length - 1)),
                    ...ui,
                },
                ...rest,
            },
        };
    }, {});

    const newCapitalCosts = Object.entries(state.capitalCosts).map(([modelId, packages]) => {
        const { [action.packageId]: _, ...remainingPackages } = packages;
        return [modelId, remainingPackages];
    });

    return updateObject(state, {
        packages: packagesObj,
        capitalCosts: Object.fromEntries(newCapitalCosts),
    });
};

const deletePackageUpgrade = (state, action) => {
    const { [action.packageId]: thisPackage = {} } = state.packages;
    const { upgrades: { [action.upgradeId]: _, ...remainingUpgrades } = {} } = thisPackage;

    return updateObject(state, {
        packages: updateObject(state.packages, {
            [action.packageId]: {
                ...thisPackage,
                upgrades: remainingUpgrades,
            },
        }),
    });
};

const setUpgradePackages = (state, action) =>
    updateObject(state, {
        packages: action.packages,
    });

const setInitUpgradePackages = (state, action) =>
    updateObject(state, {
        initPackages: cloneDeep(action.packages),
    });

const reorderUpgradePackages = (state, action) => {
    const updatedPackages = action.packages.reduce(
        (obj, id, index) => ({
            ...obj,
            [id]: {
                ...state.packages[id],
                ui: {
                    displayOrder: index,
                },
            },
        }),
        {}
    );

    return updateObject(state, {
        packages: updatedPackages,
    });
};

const updateUpgradeCost = (state, action) => {
    const upgrades = state.packages[action.packageId].upgrades;
    return updateObject(state, {
        packages: updateObject(state.packages, {
            [action.packageId]: updateObject(state.packages[action.packageId], {
                upgrades: updateObject(upgrades, {
                    [action.upgradeId]: updateObject(upgrades[action.upgradeId], {
                        cost: updateObject(upgrades[action.upgradeId]?.cost, {
                            ...upgrades[action.upgradeId]?.cost,
                            [action.upgradeType]: {
                                total: action.total || 0,
                                communityTotals: action?.communityTotals || {},
                                items: action?.items || {},
                                communityItems: action?.communityItems || {},
                            },
                        }),
                    }),
                }),
            }),
        }),
    });
};

const updatePackageOperatingConditions = (state, action) => {
    const ui = state?.packages?.[action.packageId]?.ui || {};
    return updateObject(state, {
        packages: updateObject(state.packages, {
            [action.packageId]: updateObject(state.packages[action.packageId], {
                ui: updateObject(ui, {
                    operatingConditions: action.operatingConditions,
                }),
            }),
        }),
    });
};

const updatePackageBaseCase = (state, action) => {
    const ui = state?.packages?.[action.packageId]?.ui || {};
    return updateObject(state, {
        packages: updateObject(state.packages, {
            [action.packageId]: updateObject(state.packages[action.packageId], {
                ui: updateObject(ui, {
                    relativeBaseCase: action.relativeBaseCase,
                }),
            }),
        }),
    });
};

const updateUpgradeSnapshot = (state, action) => {
    return updateObject(state, {
        initPackagesSnapshot: cloneDeep(action.upgrades),
    });
};

const initCapitalCosts = (state, action) => {
    return updateObject(state, {
        capitalCosts: action.payload.capitalCosts,
    });
};

// Remove model from capitalCosts and each package upgrade cost item
const removeCommModelPackageCosts = (state, action) => {
    const { modelId } = action.payload;

    /* 1. Remove model from capitalCost object */
    const { [modelId]: _, ...remainingModels } = state.capitalCosts;

    /* 2. Remove model from each package's cost items object */
    const updatedPackages = cloneDeep(state.packages) || {};
    Object.keys(updatedPackages).forEach((packageId) => {
        const { upgrades = {} } = updatedPackages[packageId];

        Object.keys(upgrades).forEach((upgradeId) => {
            const { cost = {} } = upgrades[upgradeId];

            Object.keys(cost).forEach((upgradeType) => {
                const { items = {}, communityItems = {}, communityTotals = {} } = cost[upgradeType];

                Object.keys(items).forEach((itemId) => {
                    delete communityItems[itemId]?.total[modelId];
                });
                delete communityTotals[modelId];
            });
        });
    });

    return updateObject(state, {
        capitalCosts: remainingModels,
        packages: updatedPackages,
    });
};

// Calculate each package's cost on the new model added and store model totals & areas to each package's upgrade
const addCommModelPackageCost = (state, action) => {
    const { upgrades = {}, components = {}, modelId = "", packageId = "" } = action.payload;

    // Clone packages and capitalCosts to avoid mutating original state
    const updatedPackages = cloneDeep(state.packages);
    const updatedCapitalCosts = cloneDeep(state.capitalCosts);

    Object.entries(upgrades).forEach(([upgradeId, upgrade]) => {
        /* 1. Add model to each package's cost items object */
        // For each upgrade, get the cost and upgrade types
        const { cost = {}, fields = {}, upgradeType: upgradeH2kType = "", upgradeSubType: upgradeType = "" } = upgrade;

        // Select all components this upgrade is to be applied to
        const selectedComponents = getSelectedComponents(upgradeType, upgradeH2kType, components);

        Object.entries(cost).forEach(([tableUpgradeType, { total = 0, items = {} }]) => {
            const costToUpdate = updatedPackages[packageId]?.upgrades[upgradeId]?.cost[tableUpgradeType] || {};

            const totalPvCapacity = tableUpgradeType === "generation" ? fields?.pvCapacity : null;

            const { newCost, totalUpgradeCost } = addUpgradeCostToPackage(costToUpdate, modelId, tableUpgradeType, totalPvCapacity, selectedComponents, components);

            if (packageId in updatedPackages) {
                updatedPackages[packageId].upgrades[upgradeId].cost[tableUpgradeType] = newCost;
            }
            /* 2. Add upgrade cost to model's capitalCost object or initialize if package not added yet */
            if (modelId in updatedCapitalCosts) {
                updatedCapitalCosts[modelId][packageId] += totalUpgradeCost;
            } else {
                updatedCapitalCosts[modelId] = { [packageId]: totalUpgradeCost };
            }
        });
    });

    return updateObject(state, {
        packages: updatedPackages,
        capitalCosts: updatedCapitalCosts,
    });
};

const addUpgradeCostToPackage = (costToUpdate, modelId, tableUpgradeType, totalPvCapacity, selectedComponents, components) => {
    const { items: costItems = {}, communityItems = {}, communityTotals = {} } = costToUpdate;
    const { preTaxTotal = 0 } = calcUpgradeDetailedCost(costItems);

    const updatedCommItems = cloneDeep(communityItems);
    const updatedCommTotals = cloneDeep(communityTotals);

    updatedCommTotals[modelId] = 0;

    // For each cost item, add new model to communityTotals and communityItems.total
    Object.entries(costItems).forEach(([itemId, { label = "", total = 0, pricePerUnit = 0, tax = 0 }]) => {
        var newModelTotal = 0;
        if (overallCostTypes.includes(label)) {
            newModelTotal = total;
        } else if (label === "tax") {
            newModelTotal = parseFloat(((tax / 100) * preTaxTotal).toFixed(2));
        } else if (tableUpgradeType === "generation") {
            newModelTotal = parseFloat((pricePerUnit * totalPvCapacity).toFixed(2)); //calcCostPerUnitBasis
        } else {
            const unitValueForCalculation = ["matCostWindow", "matCostDoor", "matCostArea"].includes(label) 
                ? parseFloat(getCompAreaForCostCalc(selectedComponents, components, tableUpgradeType, label).toFixed(2))
                : 0;

            newModelTotal = parseFloat((pricePerUnit * unitValueForCalculation).toFixed(2));
        }

        if ("total" in updatedCommItems[itemId]) {
            updatedCommItems[itemId].total[modelId] = newModelTotal;
        } else {
            updatedCommItems[itemId].total = { [modelId]: newModelTotal };
        }
        updatedCommTotals[modelId] += newModelTotal;
    });

    return {
        newCost: {
            ...costToUpdate,
            communityItems: updatedCommItems,
            communityTotals: updatedCommTotals,
        },
        totalUpgradeCost: updatedCommTotals[modelId],
    };
};

const updatePackageCost = (state, action) => {
    return updateObject(state, {
        capitalCosts: {
            ...state.capitalCosts,
            [action.payload.modelId]: {
                ...state.capitalCosts[action.payload.modelId],
                [action.payload.packageId]: action.payload.amount || 0,
            },
        },
    });
};
